import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, AlertTriangleIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, RadialBarChart, RadialBar, PolarAngleAxis } from 'recharts';
const DocumentationStatusDashboard: React.FC = () => {
  const kpis = [{
    title: 'SOP Completion %',
    value: '92%',
    change: '+6%',
    isPositive: true,
    subtitle: 'Standard Operating Procedures'
  }, {
    title: 'Pending Revisions',
    value: '18',
    change: '-5',
    isPositive: true,
    subtitle: 'Requiring updates'
  }, {
    title: 'Outdated Documents %',
    value: '8%',
    change: '-3%',
    isPositive: true,
    subtitle: 'Beyond review date'
  }];
  // Documentation Completion Gauge Data
  const documentationCompletionData = [{
    name: 'Quality SOPs',
    value: 95,
    fill: '#10b981'
  }, {
    name: 'Process SOPs',
    value: 92,
    fill: '#3b82f6'
  }, {
    name: 'Safety SOPs',
    value: 88,
    fill: '#f59e0b'
  }, {
    name: 'Maintenance SOPs',
    value: 90,
    fill: '#8b5cf6'
  }];
  // Revision Control Trend Data
  const revisionControlTrendData = [{
    month: 'Jan',
    revised: 12,
    pending: 25,
    outdated: 15
  }, {
    month: 'Feb',
    revised: 15,
    pending: 23,
    outdated: 14
  }, {
    month: 'Mar',
    revised: 18,
    pending: 21,
    outdated: 12
  }, {
    month: 'Apr',
    revised: 20,
    pending: 20,
    outdated: 11
  }, {
    month: 'May',
    revised: 22,
    pending: 19,
    outdated: 9
  }, {
    month: 'Jun',
    revised: 25,
    pending: 18,
    outdated: 8
  }];
  // Department-wise Doc Status Data
  const departmentDocStatusData = [{
    department: 'Quality',
    current: 85,
    pending: 8,
    outdated: 7,
    total: 100
  }, {
    department: 'Production',
    current: 92,
    pending: 4,
    outdated: 6,
    total: 102
  }, {
    department: 'Maintenance',
    current: 78,
    pending: 12,
    outdated: 10,
    total: 100
  }, {
    department: 'Safety',
    current: 88,
    pending: 6,
    outdated: 8,
    total: 102
  }, {
    department: 'Supply Chain',
    current: 82,
    pending: 10,
    outdated: 8,
    total: 100
  }, {
    department: 'Engineering',
    current: 90,
    pending: 5,
    outdated: 7,
    total: 102
  }];
  // Documentation Register Data
  const documentationRegisterData = [{
    docId: 'QMS-SOP-001',
    documentTitle: 'Incoming Material Inspection Procedure',
    department: 'Quality',
    category: 'Quality SOP',
    version: 'Rev 3.2',
    issueDate: '2023-08-15',
    reviewDate: '2024-08-15',
    nextReviewDate: '2025-08-15',
    owner: 'Quality Manager',
    approver: 'Plant Head',
    status: 'Current',
    location: 'Quality/Inspection',
    distributionList: 'QC Team, Warehouse',
    remarks: 'Updated with new measurement equipment calibration requirements'
  }, {
    docId: 'PRD-SOP-012',
    documentTitle: 'Line Changeover Procedure - Assembly Line 3',
    department: 'Production',
    category: 'Process SOP',
    version: 'Rev 2.5',
    issueDate: '2024-01-10',
    reviewDate: '2025-01-10',
    nextReviewDate: '2026-01-10',
    owner: 'Production Supervisor',
    approver: 'Operations Manager',
    status: 'Current',
    location: 'Production/Assembly',
    distributionList: 'Line Operators, Supervisors',
    remarks: 'New product variant changeover steps added'
  }, {
    docId: 'SAF-SOP-005',
    documentTitle: 'Emergency Evacuation Procedure',
    department: 'Safety',
    category: 'Safety SOP',
    version: 'Rev 4.0',
    issueDate: '2023-06-20',
    reviewDate: '2024-06-20',
    nextReviewDate: '2025-06-20',
    owner: 'Safety Officer',
    approver: 'EHS Manager',
    status: 'Current',
    location: 'Safety/Emergency',
    distributionList: 'All Employees',
    remarks: 'Updated evacuation routes and assembly points'
  }, {
    docId: 'MNT-SOP-008',
    documentTitle: 'Preventive Maintenance Schedule - CNC Machines',
    department: 'Maintenance',
    category: 'Maintenance SOP',
    version: 'Rev 2.0',
    issueDate: '2023-12-05',
    reviewDate: '2024-12-05',
    nextReviewDate: '2025-12-05',
    owner: 'Maintenance Manager',
    approver: 'Plant Head',
    status: 'Pending Revision',
    location: 'Maintenance/PM',
    distributionList: 'Maintenance Team',
    remarks: 'New CNC equipment added, schedule needs update'
  }, {
    docId: 'QMS-SOP-015',
    documentTitle: 'Control Plan Management Procedure',
    department: 'Quality',
    category: 'Quality SOP',
    version: 'Rev 3.0',
    issueDate: '2023-03-12',
    reviewDate: '2024-03-12',
    nextReviewDate: '2025-03-12',
    owner: 'Quality Engineer',
    approver: 'Quality Manager',
    status: 'Outdated',
    location: 'Quality/Planning',
    distributionList: 'Quality Team, Production',
    remarks: 'Review overdue by 45 days - update in progress'
  }, {
    docId: 'SCM-SOP-003',
    documentTitle: 'Supplier Evaluation & Selection Procedure',
    department: 'Supply Chain',
    category: 'Process SOP',
    version: 'Rev 2.8',
    issueDate: '2024-02-18',
    reviewDate: '2025-02-18',
    nextReviewDate: '2026-02-18',
    owner: 'Sourcing Manager',
    approver: 'Supply Chain Head',
    status: 'Current',
    location: 'Supply Chain/Vendor',
    distributionList: 'Sourcing Team, Quality',
    remarks: 'ESG criteria incorporated in evaluation matrix'
  }, {
    docId: 'ENG-SOP-007',
    documentTitle: 'Design FMEA Preparation Guideline',
    department: 'Engineering',
    category: 'Process SOP',
    version: 'Rev 1.5',
    issueDate: '2023-09-25',
    reviewDate: '2024-09-25',
    nextReviewDate: '2025-09-25',
    owner: 'Design Engineer',
    approver: 'Engineering Manager',
    status: 'Current',
    location: 'Engineering/Design',
    distributionList: 'Engineering Team, Quality',
    remarks: 'Aligned with latest AIAG-VDA FMEA handbook'
  }, {
    docId: 'PRD-SOP-025',
    documentTitle: 'First Piece Inspection Procedure',
    department: 'Production',
    category: 'Quality SOP',
    version: 'Rev 3.1',
    issueDate: '2023-11-08',
    reviewDate: '2024-11-08',
    nextReviewDate: '2025-11-08',
    owner: 'QC Inspector',
    approver: 'Quality Manager',
    status: 'Pending Revision',
    location: 'Quality/Inspection',
    distributionList: 'QC Team, Production',
    remarks: 'Digital inspection system integration pending'
  }, {
    docId: 'SAF-SOP-012',
    documentTitle: 'Chemical Handling & Storage Procedure',
    department: 'Safety',
    category: 'Safety SOP',
    version: 'Rev 2.3',
    issueDate: '2023-04-15',
    reviewDate: '2024-04-15',
    nextReviewDate: '2025-04-15',
    owner: 'Safety Officer',
    approver: 'EHS Manager',
    status: 'Outdated',
    location: 'Safety/Chemical',
    distributionList: 'Warehouse, Production',
    remarks: 'New chemical storage facility requires procedure update'
  }, {
    docId: 'MNT-SOP-015',
    documentTitle: 'Tool Calibration & Verification Procedure',
    department: 'Maintenance',
    category: 'Maintenance SOP',
    version: 'Rev 1.8',
    issueDate: '2024-01-22',
    reviewDate: '2025-01-22',
    nextReviewDate: '2026-01-22',
    owner: 'Tool Room Manager',
    approver: 'Maintenance Manager',
    status: 'Current',
    location: 'Maintenance/Calibration',
    distributionList: 'Tool Room, Quality Lab',
    remarks: 'New calibration equipment procedures added'
  }, {
    docId: 'QMS-SOP-022',
    documentTitle: 'Customer Complaint Handling Procedure',
    department: 'Quality',
    category: 'Quality SOP',
    version: 'Rev 4.5',
    issueDate: '2023-10-30',
    reviewDate: '2024-10-30',
    nextReviewDate: '2025-10-30',
    owner: 'Customer Quality Manager',
    approver: 'Quality Head',
    status: 'Pending Revision',
    location: 'Quality/Customer',
    distributionList: 'Customer Service, Quality',
    remarks: 'New customer portal integration requirements'
  }, {
    docId: 'SCM-SOP-010',
    documentTitle: 'Import/Export Documentation Procedure',
    department: 'Supply Chain',
    category: 'Process SOP',
    version: 'Rev 2.2',
    issueDate: '2023-07-18',
    reviewDate: '2024-07-18',
    nextReviewDate: '2025-07-18',
    owner: 'Logistics Manager',
    approver: 'Supply Chain Head',
    status: 'Pending Revision',
    location: 'Supply Chain/Logistics',
    distributionList: 'Logistics Team, Customs',
    remarks: 'Updated customs regulations need incorporation'
  }, {
    docId: 'ENG-SOP-018',
    documentTitle: 'Engineering Change Management Procedure',
    department: 'Engineering',
    category: 'Process SOP',
    version: 'Rev 3.8',
    issueDate: '2024-03-05',
    reviewDate: '2025-03-05',
    nextReviewDate: '2026-03-05',
    owner: 'Change Control Manager',
    approver: 'Engineering Head',
    status: 'Current',
    location: 'Engineering/Change',
    distributionList: 'All Departments',
    remarks: 'Digital ECR system workflow incorporated'
  }, {
    docId: 'PRD-SOP-032',
    documentTitle: 'Material Traceability Procedure',
    department: 'Production',
    category: 'Quality SOP',
    version: 'Rev 2.7',
    issueDate: '2023-05-12',
    reviewDate: '2024-05-12',
    nextReviewDate: '2025-05-12',
    owner: 'Production Planner',
    approver: 'Operations Manager',
    status: 'Outdated',
    location: 'Production/Planning',
    distributionList: 'Production, Warehouse',
    remarks: 'Barcode system upgrade requires procedure revision'
  }, {
    docId: 'SAF-SOP-020',
    documentTitle: 'Contractor Safety Management Procedure',
    department: 'Safety',
    category: 'Safety SOP',
    version: 'Rev 1.5',
    issueDate: '2024-02-08',
    reviewDate: '2025-02-08',
    nextReviewDate: '2026-02-08',
    owner: 'Safety Officer',
    approver: 'EHS Manager',
    status: 'Current',
    location: 'Safety/Contractor',
    distributionList: 'Security, Maintenance',
    remarks: 'Enhanced contractor induction requirements added'
  }];
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Current':
        return 'bg-green-100 text-green-700';
      case 'Pending Revision':
        return 'bg-yellow-100 text-yellow-700';
      case 'Outdated':
        return 'bg-red-100 text-red-700';
      case 'Under Review':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Quality SOP':
        return 'bg-blue-100 text-blue-700';
      case 'Process SOP':
        return 'bg-purple-100 text-purple-700';
      case 'Safety SOP':
        return 'bg-orange-100 text-orange-700';
      case 'Maintenance SOP':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  return <div data-id="element-1655">
      <DescriptionPanel title="Documentation Status" description="Comprehensive documentation management system tracking SOPs, work instructions, and quality documents across all departments. Monitor completion rates, revision control, and ensure all documentation remains current and compliant with ISO and IATF requirements." insights={['SOP completion rate at 92% - significant improvement from 86% last quarter', 'Pending revisions reduced to 18 from 23 - focused effort on documentation updates', 'Outdated documents at 8% - 3 documents requiring immediate attention', 'Production department leading with 92% current documentation rate']} data-id="element-1656" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-1657">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1658">
          Documentation Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1659">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-1660">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1661">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-3" data-id="element-1662">{kpi.subtitle}</div>
              <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-1663">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-1664">
                {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-1665" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-1666" />}
                {kpi.change}
              </div>
            </Card>)}
        </div>
      </div>
      {/* Documentation Completion Gauge and Revision Control Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-1667">
        {/* Documentation Completion Gauge */}
        <Card className="p-6" data-id="element-1668">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-1669">
            Documentation Completion Gauge
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-1670">
            Completion status across different SOP categories
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-1671">
            <RadialBarChart cx="50%" cy="50%" innerRadius="20%" outerRadius="90%" data={documentationCompletionData} startAngle={180} endAngle={0} data-id="element-1672">
              <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} data-id="element-1673" />
              <RadialBar background dataKey="value" cornerRadius={10} label={{
              position: 'insideStart',
              fill: '#fff',
              fontSize: 14,
              fontWeight: 'bold'
            }} data-id="element-1674" />
              <Legend iconSize={10} layout="vertical" verticalAlign="middle" align="right" data-id="element-1675" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-1676" />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1677">
            <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-1678">
              <div className="text-center" data-id="element-1679">
                <div className="text-gray-600" data-id="element-1680">Quality SOPs</div>
                <div className="text-lg font-bold text-green-600" data-id="element-1681">95%</div>
                <div className="text-xs text-gray-500" data-id="element-1682">complete</div>
              </div>
              <div className="text-center" data-id="element-1683">
                <div className="text-gray-600" data-id="element-1684">Process SOPs</div>
                <div className="text-lg font-bold text-blue-600" data-id="element-1685">92%</div>
                <div className="text-xs text-gray-500" data-id="element-1686">complete</div>
              </div>
              <div className="text-center" data-id="element-1687">
                <div className="text-gray-600" data-id="element-1688">Safety SOPs</div>
                <div className="text-lg font-bold text-orange-600" data-id="element-1689">88%</div>
                <div className="text-xs text-gray-500" data-id="element-1690">complete</div>
              </div>
              <div className="text-center" data-id="element-1691">
                <div className="text-gray-600" data-id="element-1692">Maintenance</div>
                <div className="text-lg font-bold text-purple-600" data-id="element-1693">90%</div>
                <div className="text-xs text-gray-500" data-id="element-1694">complete</div>
              </div>
            </div>
          </div>
        </Card>
        {/* Revision Control Trend */}
        <Card className="p-6" data-id="element-1695">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-1696">
            Revision Control Trend
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-1697">
            Monthly trend of document revisions and pending updates
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-1698">
            <LineChart data={revisionControlTrendData} data-id="element-1699">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-1700" />
              <XAxis dataKey="month" tick={{
              fontSize: 12
            }} data-id="element-1701" />
              <YAxis tick={{
              fontSize: 12
            }} data-id="element-1702" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-1703" />
              <Legend data-id="element-1704" />
              <Line type="monotone" dataKey="revised" stroke="#10b981" strokeWidth={3} name="Revised" dot={{
              r: 5
            }} data-id="element-1705" />
              <Line type="monotone" dataKey="pending" stroke="#f59e0b" strokeWidth={3} name="Pending" dot={{
              r: 5
            }} data-id="element-1706" />
              <Line type="monotone" dataKey="outdated" stroke="#ef4444" strokeWidth={3} name="Outdated" dot={{
              r: 5
            }} data-id="element-1707" />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1708">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-1709">
              <div className="text-center" data-id="element-1710">
                <div className="text-gray-600" data-id="element-1711">Revised This Month</div>
                <div className="text-xl font-bold text-green-600" data-id="element-1712">25</div>
                <div className="text-xs text-gray-500" data-id="element-1713">documents</div>
              </div>
              <div className="text-center" data-id="element-1714">
                <div className="text-gray-600" data-id="element-1715">Pending</div>
                <div className="text-xl font-bold text-orange-600" data-id="element-1716">18</div>
                <div className="text-xs text-gray-500" data-id="element-1717">awaiting update</div>
              </div>
              <div className="text-center" data-id="element-1718">
                <div className="text-gray-600" data-id="element-1719">Outdated</div>
                <div className="text-xl font-bold text-red-600" data-id="element-1720">8</div>
                <div className="text-xs text-gray-500" data-id="element-1721">overdue</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* Department-wise Doc Status */}
      <Card className="p-6 mb-8" data-id="element-1722">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-1723">
          Department-wise Doc Status
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-1724">
          Documentation status breakdown by department
        </p>
        <ResponsiveContainer width="100%" height={400} data-id="element-1725">
          <BarChart data={departmentDocStatusData} data-id="element-1726">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-1727" />
            <XAxis dataKey="department" tick={{
            fontSize: 12
          }} data-id="element-1728" />
            <YAxis tick={{
            fontSize: 12
          }} data-id="element-1729" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} data-id="element-1730" />
            <Legend data-id="element-1731" />
            <Bar dataKey="current" stackId="a" fill="#10b981" name="Current" data-id="element-1732" />
            <Bar dataKey="pending" stackId="a" fill="#f59e0b" name="Pending Revision" data-id="element-1733" />
            <Bar dataKey="outdated" stackId="a" fill="#ef4444" name="Outdated" data-id="element-1734" />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1735">
          <div className="grid grid-cols-6 gap-4 text-sm" data-id="element-1736">
            {departmentDocStatusData.map((dept, index) => <div key={index} className="text-center" data-id="element-1737">
                <div className="text-gray-600" data-id="element-1738">{dept.department}</div>
                <div className="text-lg font-bold text-gray-900" data-id="element-1739">
                  {dept.total}
                </div>
                <div className="text-xs text-green-600" data-id="element-1740">
                  {Math.round(dept.current / dept.total * 100)}% current
                </div>
              </div>)}
          </div>
        </div>
      </Card>
      {/* Documentation Register */}
      <Card className="p-6" data-id="element-1741">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-1742">
          Documentation Register
        </h3>
        <div className="overflow-x-auto" data-id="element-1743">
          <table className="w-full" data-id="element-1744">
            <thead data-id="element-1745">
              <tr className="border-b-2 border-gray-300" data-id="element-1746">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1747">
                  Document ID
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1748">
                  Document Title
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1749">
                  Department
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1750">
                  Category
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1751">
                  Version
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1752">
                  Issue Date
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1753">
                  Review Date
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1754">
                  Next Review
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1755">
                  Owner
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1756">
                  Approver
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1757">
                  Status
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1758">
                  Location
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1759">
                  Distribution
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1760">
                  Remarks
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1761">
              {documentationRegisterData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-1762">
                  <td className="py-3 px-4 text-sm font-medium text-blue-600" data-id="element-1763">
                    {row.docId}
                  </td>
                  <td className="py-3 px-4 text-sm font-medium text-gray-800 max-w-xs" data-id="element-1764">
                    {row.documentTitle}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1765">
                    {row.department}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-1766">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getCategoryColor(row.category)}`} data-id="element-1767">
                      {row.category}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center text-sm font-semibold text-gray-900" data-id="element-1768">
                    {row.version}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1769">
                    {row.issueDate}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1770">
                    {row.reviewDate}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1771">
                    {row.nextReviewDate}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1772">
                    {row.owner}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1773">
                    {row.approver}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-1774">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getStatusColor(row.status)}`} data-id="element-1775">
                      {row.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1776">
                    {row.location}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-1777">
                    {row.distributionList}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-1778">
                    {row.remarks}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-1779">
          <div className="grid grid-cols-6 gap-4 text-sm" data-id="element-1780">
            <div className="text-center" data-id="element-1781">
              <div className="text-gray-600" data-id="element-1782">Total Documents</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-1783">15</div>
              <div className="text-xs text-gray-500" data-id="element-1784">tracked</div>
            </div>
            <div className="text-center" data-id="element-1785">
              <div className="text-gray-600" data-id="element-1786">Current</div>
              <div className="text-xl font-bold text-green-600" data-id="element-1787">10</div>
              <div className="text-xs text-gray-500" data-id="element-1788">66.7%</div>
            </div>
            <div className="text-center" data-id="element-1789">
              <div className="text-gray-600" data-id="element-1790">Pending Revision</div>
              <div className="text-xl font-bold text-yellow-600" data-id="element-1791">4</div>
              <div className="text-xs text-gray-500" data-id="element-1792">26.7%</div>
            </div>
            <div className="text-center" data-id="element-1793">
              <div className="text-gray-600" data-id="element-1794">Outdated</div>
              <div className="text-xl font-bold text-red-600" data-id="element-1795">3</div>
              <div className="text-xs text-gray-500" data-id="element-1796">20.0%</div>
            </div>
            <div className="text-center" data-id="element-1797">
              <div className="text-gray-600" data-id="element-1798">Quality SOPs</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-1799">4</div>
              <div className="text-xs text-gray-500" data-id="element-1800">26.7%</div>
            </div>
            <div className="text-center" data-id="element-1801">
              <div className="text-gray-600" data-id="element-1802">Process SOPs</div>
              <div className="text-xl font-bold text-purple-600" data-id="element-1803">7</div>
              <div className="text-xs text-gray-500" data-id="element-1804">46.7%</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default DocumentationStatusDashboard;